import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

export interface EditedAtExtensionOptions {
	saveTimestamp: boolean;
}

export const EditedAtExtension = Extension.create<EditedAtExtensionOptions>({
	name: 'editedAt',

	addOptions() {
		return {
			saveTimestamp: false,
		};
	},

	addGlobalAttributes() {
		// Only add attributes if timestamp tracking is enabled
		if (!this.options.saveTimestamp) {
			return [];
		}

		return [
			{
				types: ['paragraph', 'heading'],
				attributes: {
					'data-edited-at': {
						default: null,
						parseHTML: (el) => el.getAttribute('data-edited-at'),
						renderHTML: (attrs) => (attrs['data-edited-at'] ? { 'data-edited-at': attrs['data-edited-at'] } : {}),
					},
				},
			},
		];
	},

	addStorage() {
		return {
			nodeContentForTimestamp: new Map<string, string>(), // Track node content by node-id
			trackedNodesForTimestamp: new Set<string>(), // Track which nodes we've seen before
		};
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				appendTransaction: (transactions, oldState, newState) => {
					const { saveTimestamp } = this.options;
					if (!saveTimestamp || !transactions.some((tx) => tx.docChanged)) {
						return null;
					}

					const tr = newState.tr;
					let modified = false;

					// Initialize storage if it doesn't exist
					if (!this.editor.storage.nodeContentForTimestamp) {
						this.editor.storage.nodeContentForTimestamp = new Map<string, string>();
					}
					if (!this.editor.storage.trackedNodesForTimestamp) {
						this.editor.storage.trackedNodesForTimestamp = new Set<string>();
					}
					const nodeContent = this.editor.storage.nodeContentForTimestamp as Map<string, string>;
					const trackedNodes = this.editor.storage.trackedNodesForTimestamp as Set<string>;

					// Track IDs seen in this transaction to detect duplicates from splits
					const idsInThisTransaction = new Set<string>();

					// Helper function to get node text content
					const getNodeTextContent = (node: any): string => {
						return node.textContent || '';
					};

					newState.doc.descendants((node, pos) => {
						if (!node.isBlock || !['paragraph', 'heading'].includes(node.type.name)) {
							return true;
						}

						const nodeId = node.attrs['data-node-id'];
						const createdBy = node.attrs['data-created-by'];

						// Only track edits for nodes that already exist (have node-id and created-by)
						if (!nodeId || !createdBy) {
							return true;
						}

						// Check for duplicate IDs in this transaction (indicates a split)
						const isDuplicateInTransaction = idsInThisTransaction.has(nodeId);

						if (isDuplicateInTransaction) {
							// This is a duplicate ID from a split
							// Set edited-at to current timestamp since this is a new node being created
							const timestamp = new Date().toISOString();
							const newAttrs = {
								...node.attrs,
								'data-edited-at': timestamp,
							};
							tr.setNodeMarkup(pos, node.type, newAttrs);
							modified = true;
							// Skip further processing - CreatedByUserExtension will handle the new ID
							return true;
						}

						// Add to transaction tracking
						idsInThisTransaction.add(nodeId);

						// Check if this is an existing node we've tracked before
						const isExistingNode = trackedNodes.has(nodeId);

						if (isExistingNode) {
							// This is a node we've seen before - check for content changes
							const currentContent = getNodeTextContent(node);
							const previousContent = nodeContent.get(nodeId);

							// If content has actually changed, update the timestamp
							if (previousContent !== undefined && previousContent !== currentContent) {
								const timestamp = new Date().toISOString();
								const newAttrs = {
									...node.attrs,
									'data-edited-at': timestamp,
								};
								tr.setNodeMarkup(pos, node.type, newAttrs);
								modified = true;
							}

							// Update stored content
							nodeContent.set(nodeId, currentContent);
						} else {
							// This is the first time we're seeing this node
							trackedNodes.add(nodeId);

							// Store initial content
							const currentContent = getNodeTextContent(node);
							nodeContent.set(nodeId, currentContent);

							// Initialize data-edited-at for newly discovered nodes
							if (!node.attrs['data-edited-at']) {
								// If the node was just created, use the created-at timestamp if available
								// Otherwise use current timestamp
								const timestamp = node.attrs['data-created-at'] || new Date().toISOString();
								const newAttrs = {
									...node.attrs,
									'data-edited-at': timestamp,
								};
								tr.setNodeMarkup(pos, node.type, newAttrs);
								modified = true;
							}
						}

						return true;
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
